// src/components/CreateAccountModal.jsx
import React, { useState } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff, CheckCircle2, Loader2 } from 'lucide-react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import AceiteAtivo, { registrarAceite } from './AceiteAtivo';
import { mensagemErroCriarConta } from '../services/mensagensErro';

export default function CreateAccountModal({
  isOpen,
  onClose,
  onSuccess,
  planId,
  planName,
  planPrice,
  formData,
  report,
  finance,
  dynamicInsights
}) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [aceite, setAceite] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!aceite) { setError('Marque a caixa de aceite para continuar'); return; }
    setError('');

    // Validações
    if (!nome.trim()) {
      setError('Por favor, informe seu nome completo');
      return;
    }
    if (!email.trim()) {
      setError('Por favor, informe seu e-mail');
      return;
    }
    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setIsLoading(true);

    try {
      // 1. Criar usuário no Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      registrarAceite('criar_conta', planId);

      // 2. Determinar o role baseado no plano
      const isConsultorPlan = planId === 'advisor' || planId === 'boutique' || planId === 'enterprise';
      const role = isConsultorPlan ? 'consultor' : 'gestor';

      // 🔥 CORRIGIDO — VULNERABILIDADE CRÍTICA (achada em teste real com Asaas
      // de produção, 2026-09-28): este modal se chama "Pagamento Aprovado!" e
      // é aberto direto ao clicar em "upgrade" — SEM nenhum pagamento
      // acontecer antes. Ele gravava `plan: planId` no Firestore só por
      // criar a conta, liberando qualquer plano pago de graça. Removido o
      // campo `plan`/`consultorPlan` daqui: só a Cloud Function
      // `webhookAsaas` (servidor, depois que o Asaas confirma o dinheiro de
      // verdade) pode escrever esse campo. Este modal agora só cria a conta;
      // o plano só é liberado de verdade após o pagamento real ser feito e
      // confirmado pelo webhook.
      // 3. Salvar no Firestore
      await setDoc(doc(db, 'usuarios', user.uid), {
        uid: user.uid,
        nome: nome,
        email: email,
        role: role,
        createdAt: new Date().toISOString(),
        // Salvar snapshot do diagnóstico atual
        lastDiagnosis: {
          companyName: formData?.companyName || '',
          segment: formData?.segment || '',
          ebitda: report?.ebitda || 0,
          equityValue: finance?.equityValue || 0,
          date: new Date().toISOString()
        }
      });

      // 4. Salvar no localStorage
      localStorage.setItem('phoenix_user_type', role);
      localStorage.setItem('phoenix_user_email', email);
      localStorage.setItem('phoenix_user_name', nome);
      if (isConsultorPlan) {
        localStorage.setItem('phoenix_consultor_email', email);
      } else {
        localStorage.setItem('phoenix_gestor_email', email);
      }

      // 5. Chamar callback de sucesso
      onSuccess({ user: userCredential.user, role, plan: planId });

    } catch (err) {
      console.error('Erro ao criar conta:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('Este e-mail já está cadastrado. Faça login ou use outro e-mail.');
      } else {
        setError(mensagemErroCriarConta(err.code));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-[#010208]/95 backdrop-blur-xl flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-[#020617] border border-lime-400/30 rounded-3xl max-w-md w-full shadow-[0_0_50px_rgba(190,242,100,0.15)] overflow-hidden">

        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-lime-400/10 rounded-full flex items-center justify-center">
              <CheckCircle2 size={20} className="text-lime-400" />
            </div>
            <div>
              {/* 🔥 CORRIGIDO: dizia "Pagamento Aprovado!" mesmo sem nenhum
                  pagamento ter acontecido ainda (ver correção acima). */}
              <h3 className="text-lg font-black text-white uppercase italic tracking-tighter">Crie sua Conta</h3>
              <p className="text-[11px] text-slate-400">Plano {planName} - R$ {planPrice}/mês</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-lime-400 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="text-center mb-6">
            <p className="text-xs text-slate-400">
              Crie sua conta pra continuar — o plano {planName} é liberado assim que o pagamento for confirmado.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block mb-1">
                Nome completo
              </label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  className="w-full bg-slate-900/40 border border-white/10 pl-10 pr-3 py-3 rounded-xl text-white text-sm outline-none focus:border-lime-400/50 transition-all"
                  placeholder="Seu nome"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block mb-1">
                E-mail
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-slate-900/40 border border-white/10 pl-10 pr-3 py-3 rounded-xl text-white text-sm outline-none focus:border-lime-400/50 transition-all"
                  placeholder="voce@consultoria.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block mb-1">
                Senha (mínimo 6 caracteres)
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-slate-900/40 border border-white/10 pl-10 pr-10 py-3 rounded-xl text-white text-sm outline-none focus:border-lime-400/50 transition-all"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-lime-400 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest block mb-1">
                Confirmar senha
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-slate-900/40 border border-white/10 pl-10 pr-3 py-3 rounded-xl text-white text-sm outline-none focus:border-lime-400/50 transition-all"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            {error && (
              <p className="text-red-400 text-[11px] font-black text-center">{error}</p>
            )}

            <AceiteAtivo checked={aceite} onChange={setAceite} />

            <button
              type="submit"
              disabled={isLoading || !aceite}
              className="w-full bg-gradient-to-r from-lime-500 to-lime-600 text-black py-4 rounded-xl font-black uppercase text-sm shadow-[0_0_20px_rgba(190,242,100,0.3)] hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Criando conta...
                </>
              ) : (
                <>
                  <CheckCircle2 size={18} />
                  Criar Conta e Acessar Painel
                </>
              )}
            </button>
          </form>

          <p className="text-[11px] text-slate-600 text-center mt-4">
            Seus dados ficarão salvos na nuvem. Próximos acessos com email e senha.
          </p>
        </div>
      </div>
    </div>
  );
}